import { ContentListShell } from "@/components/studio/content-list-shell";
import { LifecycleActionsMenu } from "@/components/studio/lifecycle-actions-menu";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useContentLifecycle } from "@/hooks/use-content-lifecycle";
import { categoryService } from "@/lib/api/categories.service";
import { ContentTagService } from "@/lib/api/content.tag.service";
import { ContentRecipeService } from "@/lib/api/recipe.service";
import type {
  Category,
  StudioContentStatus,
  TagGroup,
  TagOption,
} from "@/lib/schema";
import {
  CONTENT_TYPE_CONFIG,
  formatDate,
  STATUS_CONFIG,
} from "@/lib/studio-constants";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Globe, Tags, UtensilsCrossed } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";

const recipeConfig = CONTENT_TYPE_CONFIG.recipe;
const PAGE_SIZE = 5;

interface RecipeListItem {
  id: string;
  categoryId: string;
  categoryName: string | null;
  tagOptionIds: string[];
  status: StudioContentStatus;
  version: number;
  createdBy: string;
  createdByName: string | null;
  authorCoachId: string | null;
  createdAt: string;
  updatedAt: string;
  primaryLanguage: string;
  primaryTitle: string;
  languageCount: number;
  languageCodes: string[];
  tagCount: number;
}

export default function RecipeListPage() {
  const [, setLocation] = useLocation();
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [filterValues, setFilterValues] = useState<Record<string, string>>({
    status: "all",
    category: "all",
    tag: "all",
  });
  const [languageFilter, setLanguageFilter] = useState("all");
  const [page, setPage] = useState(1);

  useEffect(() => {
    const t = setTimeout(() => {
      setSearchQuery(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const statusParam =
    filterValues.status !== "all"
      ? (filterValues.status as StudioContentStatus)
      : undefined;
  const categoryParam =
    filterValues.category !== "all" ? filterValues.category : undefined;
  const tagParam = filterValues.tag !== "all" ? filterValues.tag : undefined;
  const languageParam = languageFilter !== "all" ? languageFilter : undefined;
  const searchParam = searchQuery.trim() || undefined;

  const { data: recipeResponse, isLoading } = useQuery<{
    items: RecipeListItem[];
    pagination: {
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    };
  }>({
    queryKey: [
      "/content-recipes",
      {
        page,
        limit: PAGE_SIZE,
        search: searchParam,
        status: statusParam,
        categoryId: categoryParam,
        tagId: tagParam,
        language: languageParam,
      },
    ],
    queryFn: () =>
      ContentRecipeService.findAll({
        page,
        limit: PAGE_SIZE,
        search: searchParam,
        status: statusParam,
        categoryId: categoryParam,
        tagId: tagParam,
        language: languageParam,
      }) as unknown as Promise<{
        items: RecipeListItem[];
        pagination: {
          total: number;
          page: number;
          limit: number;
          totalPages: number;
        };
      }>,
    placeholderData: keepPreviousData,
  });

  const recipeItems = recipeResponse?.items ?? [];
  const totalItems = recipeResponse?.pagination.total ?? 0;
  const totalPages = recipeResponse?.pagination.totalPages ?? 1;

  const { data: categories = [] } = useQuery<Category[]>({
    queryKey: ["/content-category"],
    queryFn: () => categoryService.findAll(),
  });

  const { data: tagGroups = [] } = useQuery<TagGroup[]>({
    queryKey: ["/content/tag-groups"],
    queryFn: () => ContentTagService.getTagGroups(),
  });

  const { data: tagOptions = [] } = useQuery<TagOption[]>({
    queryKey: ["/content/tag-options"],
    queryFn: () => ContentTagService.getTagOptions(),
  });

  const { submitMutation, archiveMutation, withdrawMutation, restoreMutation } =
    useContentLifecycle({
      contentType: "recipe",
      apiPath: "/content-recipes",
      listRoute: "/studio/recipes",
      editRoute: (id) => `/studio/recipes/${id}/edit`,
    });

  const tagOptionsWithGroup = useMemo(
    () =>
      tagOptions.map((opt) => {
        const group = tagGroups.find((g) => g.id === opt.groupId);
        return { ...opt, groupName: group?.name ?? "" };
      }),
    [tagOptions, tagGroups],
  );

  const handleFilterChange = (key: string, value: string) => {
    setFilterValues((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const handleLanguageFilterChange = (value: string) => {
    setLanguageFilter(value);
    setPage(1);
  };

  const customFilters = useMemo(
    () => [
      {
        key: "category",
        label: "Categories",
        options: categories.map((cat) => ({ value: cat.id, label: cat.title })),
      },
      {
        key: "tag",
        label: "Tags",
        options: tagOptionsWithGroup.map((opt) => ({
          value: opt.id,
          label: opt.groupName ? `${opt.groupName}: ${opt.label}` : opt.label,
        })),
      },
    ],
    [categories, tagOptionsWithGroup],
  );

  const startIndex = totalItems === 0 ? 0 : (page - 1) * PAGE_SIZE;
  const endIndex = Math.min(startIndex + recipeItems.length, totalItems);

  return (
    <ContentListShell
      contentTypeConfig={recipeConfig}
      isLoading={isLoading}
      itemCount={totalItems}
      searchQuery={searchInput}
      onSearchChange={setSearchInput}
      filters={customFilters}
      filterValues={filterValues}
      onFilterChange={handleFilterChange}
      showLanguageFilter={true}
      languageFilterValue={languageFilter}
      onLanguageFilterChange={handleLanguageFilterChange}
      pagination={{
        currentPage: page,
        totalPages,
        totalItems,
        startIndex,
        endIndex,
        canGoNext: page < totalPages,
        canGoPrev: page > 1,
        onPageChange: (p) => setPage(Math.max(1, Math.min(p, totalPages))),
        onNext: () => setPage((p) => Math.min(p + 1, totalPages)),
        onPrev: () => setPage((p) => Math.max(p - 1, 1)),
      }}
    >
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[300px]">Title</TableHead>
            <TableHead>Category</TableHead>
            <TableHead className="text-center">Languages</TableHead>
            <TableHead className="text-center">Tags</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Updated</TableHead>
            <TableHead className="w-[50px]"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {recipeItems.map((item) => {
            const statusConfig = STATUS_CONFIG[item.status];
            return (
              <TableRow
                key={item.id}
                className="cursor-pointer"
                onClick={() => setLocation(`/studio/recipes/${item.id}/edit`)}
                data-testid={`row-recipe-${item.id}`}
              >
                <TableCell>
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-md bg-muted shrink-0">
                      <UtensilsCrossed className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div className="min-w-0">
                      <p
                        className="font-medium text-foreground truncate"
                        data-testid={`text-recipe-title-${item.id}`}
                      >
                        {item.primaryTitle}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        v{item.version}
                      </p>
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <span className="text-sm text-muted-foreground">
                    {item.categoryName || "—"}
                  </span>
                </TableCell>
                <TableCell className="text-center">
                  <div className="flex items-center justify-center gap-1">
                    <Globe className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">
                      {item.languageCount}
                    </span>
                  </div>
                </TableCell>
                <TableCell className="text-center">
                  <div className="flex items-center justify-center gap-1">
                    <Tags className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">
                      {item.tagCount}
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge
                    variant={statusConfig.variant}
                    className="no-default-hover-elevate no-default-active-elevate"
                  >
                    {statusConfig.label}
                  </Badge>
                </TableCell>
                <TableCell>
                  <span className="text-sm text-muted-foreground">
                    {formatDate(item.updatedAt)}
                  </span>
                </TableCell>
                <TableCell>
                  <LifecycleActionsMenu
                    itemId={item.id}
                    status={item.status}
                    createdBy={item.createdBy}
                    authorCoachId={item.authorCoachId}
                    onEdit={(id) => setLocation(`/studio/recipes/${id}/edit`)}
                    onSubmit={(id) => submitMutation.mutate(id)}
                    onArchive={(id) => archiveMutation.mutate(id)}
                    onWithdraw={(id) => withdrawMutation.mutate(id)}
                    onRestore={(id) => restoreMutation.mutate(id)}
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </ContentListShell>
  );
}
